import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Menu, Bell, Check, Clock, AlertTriangle, Info, Wrench, Sparkles, Expand, ExternalLink, X, Calendar, Tag, FileText, Building, HelpCircle } from "lucide-react";
import ChatIcon from "../Common/ChatIcon";
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  useUser,
} from "@clerk/clerk-react";
import { Select, MenuItem, FormControl } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import "./header.scss";
import vorxocLogo from "../../Images/vorxocGreen.svg";
import vorxocLogoWhite from "../../Images/vorxocWhite.svg";
import vorxocLogoBlack from "../../Images/VORXOCblack.svg";
import { fetchClients, setSelectedClientId } from "../../features/onboarding/onboardingSlice";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../../context/ThemeContext";
import { useNotifications } from "../../hooks/useNotifications";
import { useTagNotifications } from "../../hooks/useTagNotifications";

const NOTIFICATION_ICONS = {
  feature: Sparkles,
  maintenance: Wrench,
  security: AlertTriangle,
  info: Info,
  update: Check,
};

const NOTIFICATION_TYPE_LABELS = {
  feature: "Feature",
  maintenance: "Maintenance",
  security: "Security",
  info: "Information",
  update: "Update",
};

const PRIORITY_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};


export default function HeaderBar({ onToggleSidebar, isSidebarOpen }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useUser();
  const { theme } = useTheme();

  // Notification state using custom hook
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    markAsRead,
    markAllAsRead,
  } = useNotifications({ limit: 10 });

  // Tag notifications state using custom hook
  const {
    notifications: tagNotifications,
    unreadCount: tagUnreadCount,
    loading: tagNotificationsLoading,
    markAsRead: markTagAsRead,
    markAllAsRead: markAllTagsAsRead,
  } = useTagNotifications({ limit: 10 });

  const [showNotifications, setShowNotifications] = useState(false);
  const [showTagNotifications, setShowTagNotifications] = useState(false);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [isMarkingAllTagsRead, setIsMarkingAllTagsRead] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const notificationRef = useRef(null);
  const tagNotificationRef = useRef(null);

  const { clients: apiClients, clientsStatus, selectedClientId } = useSelector(
    (state) => state.onboarding
  );

  // Map API clients to the expected format { id, name }
  const clients = useMemo(() => {
    return (apiClients || []).map((client) => ({
      id: client.clientUUID || client.id,
      name: client.clientName || client.name,
    }));
  }, [apiClients]);

  // Get current client name for display
  const currentClient = useMemo(() => {
    return clients.find((client) => client.id === selectedClientId);
  }, [clients, selectedClientId]);

  // Get client name for non-Helxon users (they only have one client)
  const clientNameForDisplay = useMemo(() => {
    if (clients.length > 0) {
      return clients[0].name;
    }
    return null;
  }, [clients]);

  // Check if user is Helxon
  const isHelxonUser = user?.publicMetadata?.client?.name === "Helxon";

  // Fetch clients on mount
  useEffect(() => {
    if (clientsStatus === "idle") {
      dispatch(fetchClients());
    }
  }, [dispatch, clientsStatus]);

  // Set default selected client when clients are loaded and validate selectedClientId
  useEffect(() => {
    if (clients.length > 0) {
      // For non-Helxon users, always set to their single client
      if (!isHelxonUser) {
        dispatch(setSelectedClientId(clients[0].id));
      } else {
        // For Helxon users, validate that selectedClientId exists in clients list
        // If it doesn't exist (e.g., client was deleted), reset to null (All Clients)
        if (selectedClientId) {
          const clientExists = clients.some((client) => client.id === selectedClientId);
          if (!clientExists) {
            dispatch(setSelectedClientId(null));
          }
        }
      }
    }
  }, [clients, selectedClientId, dispatch, isHelxonUser]);

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (tagNotificationRef.current && !tagNotificationRef.current.contains(event.target)) {
        setShowTagNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle client selection change
  const handleClientChange = (e) => {
    const value = e.target.value;
    // Empty string means "All Clients" - dispatch null to clear filter
    dispatch(setSelectedClientId(value === "" ? null : value));
  };

  // Toggle notifications dropdown
  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
  };

  // Handle marking all notifications as read
  const handleMarkAllAsRead = async () => {
    setIsMarkingAllRead(true);
    try {
      await markAllAsRead();
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  // Handle marking a single notification as read
  const handleMarkAsRead = async (notificationId) => {
    await markAsRead(notificationId);
  };

  // Open notification detail modal
  const handleExpandNotification = (notification, e) => {
    e.stopPropagation();
    setSelectedNotification(notification);
    // Mark as read when expanded
    if (!notification.read) {
      markAsRead(notification.id);
    }
  };

  // Close notification detail modal
  const handleCloseNotificationModal = () => {
    setSelectedNotification(null);
  };

  // Handle documentation redirect (placeholder for future)
  const handleDocumentationRedirect = (notification, e) => {
    e.stopPropagation();
    // TODO: In the future, this will redirect to documentation URL
    // For now, we'll just log and optionally show a message
    console.log("Documentation redirect for notification:", notification.id);
    // If notification has a docUrl in the future:
    // if (notification.docUrl) {
    //   window.open(notification.docUrl, '_blank');
    // }
  };

  // Toggle tag notifications dropdown
  const handleTagNotificationClick = () => {
    setShowTagNotifications(!showTagNotifications);
    // Close other dropdown
    setShowNotifications(false);
  };

  // Handle marking all tag notifications as read
  const handleMarkAllTagsAsRead = async () => {
    setIsMarkingAllTagsRead(true);
    try {
      await markAllTagsAsRead();
    } finally {
      setIsMarkingAllTagsRead(false);
    }
  };

  // Handle clicking on a tag notification - navigate to the source
  const handleTagNotificationNavigate = async (tagNotification) => {
    // Mark as read first
    if (!tagNotification.read) {
      await markTagAsRead(tagNotification.id);
    }
    
    // Close dropdown
    setShowTagNotifications(false);
    
    // Navigate based on source type
    if (tagNotification.sourceType === "incident") {
      navigate(`/incidents/${tagNotification.sourceId}`);
    } else if (tagNotification.sourceType === "customer_request") {
      navigate(`/customer-requests/${tagNotification.sourceId}`);
    }
  };

  // Get source type icon
  const getSourceTypeIcon = (sourceType) => {
    if (sourceType === "incident") {
      return <AlertTriangle size={14} />;
    }
    return <FileText size={14} />;
  };

  // Get source type label
  const getSourceTypeLabel = (sourceType) => {
    if (sourceType === "incident") {
      return "Incident";
    }
    return "Request";
  };

  // Format date for modal
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    const IconComponent = NOTIFICATION_ICONS[type] || Info;
    return <IconComponent size={16} />;
  };

  // Get priority class
  const getPriorityClass = (priority) => {
    const classes = {
      critical: "notification-item--critical",
      high: "notification-item--high",
      medium: "notification-item--medium",
      low: "notification-item--low",
    };
    return classes[priority] || "";
  };

  return (
    <header className="soc-header" role="banner">
      <div className="soc-header__left">
        <button
          className="soc-header__burger"
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
          aria-pressed={isSidebarOpen}
        >
          <Menu size={20} />
        </button>
        <div
          className="soc-header__brand"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "2px",
          }}
        >
          <img
            src={vorxocLogo}
            alt="VORXOC"
            className="soc-header__logo-img"
            style={{ height: "40px", width: "auto" }}
          />
        </div>
        <div className="soc-header__logo-secondary" style={{ height: "19px", width: "0px" }}>
          <img
            src={theme === "light" ? vorxocLogoBlack : vorxocLogoWhite}
            alt="VORXOC"
            className="soc-header__logo-img"
            style={{ height: "17px", width: "auto" }}
          />
        </div>
      </div>

      <div className="soc-header__center"></div>

      <div className="soc-header__right">
        {/* <div className="soc-header__search">
          <input type="text" placeholder="Type here..." className="search-input" />
          <Search size={16} className="search-icon" />
        </div> */}
        <div className="clientName">
          {isHelxonUser ? (
            <FormControl size="small" className="dashboard__time-select">
              <Select
                value={selectedClientId || ""}
                onChange={handleClientChange}
                displayEmpty
                IconComponent={ArrowDropDown}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: theme === 'light' ? '#ffffff' : '#091619',
                      borderRadius: '2px',
                      '& .MuiMenuItem-root': {
                        color: theme === 'light' ? '#0f172a' : 'white',
                        '&.Mui-selected': {
                          bgcolor: theme === 'light' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(11, 98, 88, 0.7)',
                        },
                        '&.Mui-selected:hover': {
                          bgcolor: theme === 'light' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(11, 98, 88, 0.7)',
                        },
                        '&:hover': {
                          bgcolor: theme === 'light' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(11, 98, 88, 0.7)',
                        },
                      },
                    },
                  },
                }}
                disabled={clientsStatus === "loading"}
                sx={{
                  color: theme === 'light' ? '#0f172a' : 'white',
                  fontSize: "13px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme === 'light' ? 'rgba(0, 0, 0, 0.2)' : "rgba(255, 255, 255, 0.2)",
                  },
                  "&:hover": {
                    color: theme === 'light' ? '#000000' : 'white',
                    backgroundColor: theme === 'light' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(11, 98, 88, 0.3)',
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme === 'light' ? 'rgba(0, 0, 0, 0.3)' : "rgba(255, 255, 255, 0.3)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: theme === 'light' ? '#0f172a' : "white",
                  },
                  "&:hover .MuiSvgIcon-root": {
                    color: theme === 'light' ? '#000000' : "white",
                  },
                }}
                renderValue={(selected) => {
                  if (!selected || selected === "") {
                    return "All Clients";
                  }
                  const client = clients.find((c) => c.id === selected);
                  return client ? client.name : selected;
                }}
              >
                {clientsStatus === "loading" ? (
                  <MenuItem value="" disabled>
                    Loading...
                  </MenuItem>
                ) : clients.length === 0 ? (
                  <MenuItem value="" disabled>
                    No clients
                  </MenuItem>
                ) : (
                  [
                    ...clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name}
                      </MenuItem>
                    )),
                    <MenuItem key="all-clients" value="">
                      All Clients
                    </MenuItem>
                  ]
                )}
              </Select>
            </FormControl>
          ) : (
            <div
              style={{
                color: theme === 'light' ? '#0f172a' : 'white',
                fontSize: "13px",
                padding: "8px 8px",
                backgroundColor: theme === 'light' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(11, 98, 88, 0.3)',
                border: theme === 'light' ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '2px',
                minHeight: '22px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {clientsStatus === "loading" ? "Loading..." : clientNameForDisplay || "No client"}
            </div>
          )}
        </div>

        <div className="soc-header__actions">
          <ThemeToggle />
          
          {/* Tag Notifications (ChatIcon) */}
          <div className="notification-wrapper" ref={tagNotificationRef}>
            <button 
              className="soc-header__icon-btn" 
              aria-label="Messages"
              onClick={handleTagNotificationClick}
            >
              <ChatIcon size={20} />
              {tagUnreadCount > 0 && (
                <span className="notification-badge notification-badge--messages">
                  {tagUnreadCount > 9 ? "9+" : tagUnreadCount}
                </span>
              )}
            </button>

            {/* Tag Notifications Dropdown */}
            {showTagNotifications && (
              <div className="notification-dropdown notification-dropdown--tags">
                <div className="notification-dropdown__header">
                  <h4 className="notification-dropdown__title">Mentions</h4>
                  {tagUnreadCount > 0 && (
                    <button 
                      className="notification-dropdown__mark-all"
                      onClick={handleMarkAllTagsAsRead}
                      disabled={isMarkingAllTagsRead || tagNotificationsLoading}
                    >
                      {isMarkingAllTagsRead ? "Marking..." : "Mark all as read"}
                    </button>
                  )}
                </div>

                <div className="notification-dropdown__list">
                  {tagNotificationsLoading ? (
                    <div className="notification-dropdown__empty">
                      <div className="notification-dropdown__spinner" />
                      <p>Loading mentions...</p>
                    </div>
                  ) : tagNotifications.length === 0 ? (
                    <div className="notification-dropdown__empty">
                      <ChatIcon size={24} />
                      <p>No mentions yet</p>
                    </div>
                  ) : (
                    tagNotifications.map((tagNotification) => (
                      <div
                        key={tagNotification.id}
                        className={`tag-notification-item ${!tagNotification.read ? "tag-notification-item--unread" : ""}`}
                        onClick={() => handleTagNotificationNavigate(tagNotification)}
                      >
                        <div className={`tag-notification-item__icon tag-notification-item__icon--${tagNotification.sourceType}`}>
                          {getSourceTypeIcon(tagNotification.sourceType)}
                        </div>
                        <div className="tag-notification-item__content">
                          <div className="tag-notification-item__header">
                            <span className="tag-notification-item__source-type">
                              {getSourceTypeLabel(tagNotification.sourceType)}
                            </span>
                            <span className="tag-notification-item__source-id">
                              #{tagNotification.sourceId}
                            </span>
                            {!tagNotification.read && <span className="tag-notification-item__dot" />}
                          </div>
                          <p className="tag-notification-item__title">{tagNotification.sourceTitle}</p>
                          <p className="tag-notification-item__message">
                            <Building size={10} />
                            <span>Tagged by @{tagNotification.taggedBy}</span>
                          </p>
                          <span className="tag-notification-item__time">
                            <Clock size={12} />
                            {formatTimeAgo(tagNotification.createdAt)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="notification-dropdown__footer">
                  <span className="notification-dropdown__hint">
                    Click to view the conversation
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="notification-wrapper" ref={notificationRef}>
            <button 
              className="soc-header__icon-btn notification-bell-icon" 
              aria-label="Notifications"
              onClick={handleNotificationClick}
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
              )}
            </button>

            {/* Notification Dropdown */}
            {showNotifications && (
              <div className="notification-dropdown">
                <div className="notification-dropdown__header">
                  <h4 className="notification-dropdown__title">Notifications</h4>
                  {unreadCount > 0 && (
                    <button 
                      className="notification-dropdown__mark-all"
                      onClick={handleMarkAllAsRead}
                      disabled={isMarkingAllRead || notificationsLoading}
                    >
                      {isMarkingAllRead ? "Marking..." : "Mark all as read"}
                    </button>
                  )}
                </div>

                <div className="notification-dropdown__list">
                  {notificationsLoading ? (
                    <div className="notification-dropdown__empty">
                      <div className="notification-dropdown__spinner" />
                      <p>Loading notifications...</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="notification-dropdown__empty">
                      <Bell size={24} />
                      <p>No notifications</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`notification-item ${!notification.read ? "notification-item--unread" : ""} ${getPriorityClass(notification.priority)}`}
                        onClick={() => handleMarkAsRead(notification.id)}
                      >
                        <div className={`notification-item__icon notification-item__icon--${notification.type}`}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="notification-item__content">
                          <div className="notification-item__header">
                            <span className="notification-item__title">{notification.title}</span>
                            {!notification.read && <span className="notification-item__dot" />}
                          </div>
                          <p className="notification-item__message">{notification.message}</p>
                          <span className="notification-item__time">
                            <Clock size={12} />
                            {formatTimeAgo(notification.createdAt)}
                          </span>
                        </div>
                        <div className="notification-item__actions">
                          <button
                            className="notification-item__action-btn"
                            onClick={(e) => handleExpandNotification(notification, e)}
                            title="View details"
                          >
                            <Expand size={14} />
                          </button>
                          <button
                            className="notification-item__action-btn"
                            onClick={(e) => handleDocumentationRedirect(notification, e)}
                            title="View documentation"
                          >
                            <ExternalLink size={14} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="notification-dropdown__footer">
                  <a href="/system-admin" className="notification-dropdown__view-all">
                    View all notifications
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Help / Knowledge Base */}
          <button 
            className="soc-header__icon-btn soc-header__icon-btn--help" 
            aria-label="SOC Knowledge Base"
            title="SOC Knowledge Base"
            onClick={() => navigate('/knowledge')}
          >
            <HelpCircle size={22} />
          </button>

          <SignedOut>
            <SignInButton>
              <div className="soc-header__signin">
                <div className="soc-header__signin-icon">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <span>Sign In</span>
              </div>
            </SignInButton>
          </SignedOut>
          <SignedIn>
            <div className="clerk-user-button-wrapper">
              <UserButton
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: {
                      width: 32,
                      height: 32,
                    },
                    // Dropdown menu (sign out, manage account)
                    userButtonPopoverCard: {
                      backgroundColor: theme === 'light' ? '#ffffff' : '#091619',
                      border: theme === 'light' ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(56, 248, 137, 0.3)',
                      boxShadow: theme === 'light' 
                        ? '0 10px 25px -5px rgba(0, 0, 0, 0.15)' 
                        : '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
                      borderRadius: '2px',
                    },
                    userButtonPopoverActionButton: {
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                      borderRadius: '2px',
                      "&:hover": {
                        backgroundColor: theme === 'light' 
                          ? 'rgba(16, 185, 129, 0.15)' 
                          : 'rgba(11, 98, 88, 0.7)',
                        color: theme === 'light' ? '#059669' : '#39ff14',
                      },
                    },
                    userButtonPopoverActionButtonText: {
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                    },
                    userButtonPopoverActionButtonIcon: {
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                    },
                    userButtonPopoverFooter: {
                      display: "none",
                    },
                    userPreviewMainIdentifier: {
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                    },
                    userPreviewSecondaryIdentifier: {
                      color: theme === 'light' ? '#64748b' : 'rgba(245, 245, 245, 0.7)',
                    },
                    userButtonPopoverMain: {
                      backgroundColor: theme === 'light' ? '#ffffff' : '#091619',
                    },
                    // Account Management Modal
                    modalContent: {
                      backgroundColor: theme === 'light' ? '#ffffff' : '#091619',
                      borderRadius: '2px',
                      border: theme === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(56, 248, 137, 0.3)',
                    },
                    card: {
                      backgroundColor: theme === 'light' ? '#ffffff' : '#091619',
                      borderRadius: '2px',
                      boxShadow: theme === 'light'
                        ? '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                        : '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
                    },
                    navbar: {
                      backgroundColor: theme === 'light' ? '#f8fafc' : '#020a07',
                      borderRight: theme === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(56, 248, 137, 0.2)',
                    },
                    navbarButton: {
                      color: theme === 'light' ? '#475569' : '#94a3b8',
                      borderRadius: '2px',
                      "&:hover": {
                        backgroundColor: theme === 'light'
                          ? 'rgba(16, 185, 129, 0.1)'
                          : 'rgba(11, 98, 88, 0.5)',
                        color: theme === 'light' ? '#059669' : '#f5f5f5',
                      },
                    },
                    navbarButtonIcon: {
                      color: theme === 'light' ? '#475569' : '#94a3b8',
                    },
                    navbarButtonText: {
                      color: theme === 'light' ? '#475569' : '#94a3b8',
                    },
                    navbarButton__active: {
                      backgroundColor: theme === 'light'
                        ? 'rgba(16, 185, 129, 0.15)'
                        : 'rgba(11, 98, 88, 0.7)',
                      color: theme === 'light' ? '#059669' : '#39ff14',
                      borderRadius: '2px',
                    },
                    pageScrollBox: {
                      backgroundColor: theme === 'light' ? '#ffffff' : '#091619',
                    },
                    page: {
                      backgroundColor: theme === 'light' ? '#ffffff' : '#091619',
                    },
                    headerTitle: {
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                    },
                    headerSubtitle: {
                      color: theme === 'light' ? '#64748b' : '#94a3b8',
                    },
                    profileSection: {
                      backgroundColor: theme === 'light' ? '#ffffff' : '#091619',
                      borderRadius: '2px',
                    },
                    profileSectionPrimaryButton: {
                      backgroundColor: theme === 'light'
                        ? 'rgba(16, 185, 129, 0.1)'
                        : 'rgba(11, 98, 88, 0.5)',
                      color: theme === 'light' ? '#059669' : '#39ff14',
                      borderRadius: '2px',
                      border: theme === 'light' ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(56, 248, 137, 0.3)',
                      "&:hover": {
                        backgroundColor: theme === 'light'
                          ? 'rgba(16, 185, 129, 0.2)'
                          : 'rgba(11, 98, 88, 0.7)',
                      },
                    },
                    formFieldLabel: {
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                    },
                    formFieldInput: {
                      backgroundColor: theme === 'light' ? '#f8fafc' : '#020a07',
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                      border: theme === 'light' ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(56, 248, 137, 0.2)',
                      borderRadius: '2px',
                      "&:focus": {
                        borderColor: theme === 'light' ? 'rgba(16, 185, 129, 0.5)' : 'rgba(56, 248, 137, 0.5)',
                        boxShadow: theme === 'light'
                          ? '0 0 0 3px rgba(16, 185, 129, 0.1)'
                          : '0 0 0 3px rgba(56, 248, 137, 0.1)',
                      },
                    },
                    formButtonPrimary: {
                      backgroundColor: theme === 'light' ? '#10b981' : 'rgba(11, 98, 88, 0.7)',
                      color: theme === 'light' ? '#ffffff' : '#39ff14',
                      borderRadius: '2px',
                      border: theme === 'light' ? 'none' : '1px solid rgba(56, 248, 137, 0.3)',
                      "&:hover": {
                        backgroundColor: theme === 'light' ? '#059669' : 'rgba(11, 98, 88, 0.9)',
                      },
                    },
                    formButtonReset: {
                      color: theme === 'light' ? '#64748b' : '#94a3b8',
                      borderRadius: '2px',
                      "&:hover": {
                        backgroundColor: theme === 'light'
                          ? 'rgba(0, 0, 0, 0.05)'
                          : 'rgba(255, 255, 255, 0.05)',
                      },
                    },
                    badge: {
                      backgroundColor: theme === 'light'
                        ? 'rgba(16, 185, 129, 0.15)'
                        : 'rgba(56, 248, 137, 0.2)',
                      color: theme === 'light' ? '#059669' : '#39ff14',
                      borderRadius: '2px',
                    },
                    accordionTriggerButton: {
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                      borderRadius: '2px',
                      "&:hover": {
                        backgroundColor: theme === 'light'
                          ? 'rgba(0, 0, 0, 0.05)'
                          : 'rgba(255, 255, 255, 0.05)',
                      },
                    },
                    identityPreview: {
                      backgroundColor: theme === 'light' ? '#f8fafc' : '#020a07',
                      borderRadius: '2px',
                      border: theme === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(56, 248, 137, 0.2)',
                    },
                    identityPreviewText: {
                      color: theme === 'light' ? '#0f172a' : '#f5f5f5',
                    },
                    identityPreviewEditButton: {
                      color: theme === 'light' ? '#059669' : '#39ff14',
                      borderRadius: '2px',
                    },
                    menuButton: {
                      color: theme === 'light' ? '#64748b' : '#94a3b8',
                      borderRadius: '2px',
                      "&:hover": {
                        backgroundColor: theme === 'light'
                          ? 'rgba(0, 0, 0, 0.05)'
                          : 'rgba(255, 255, 255, 0.05)',
                      },
                    },
                    footer: {
                      backgroundColor: theme === 'light' ? '#f8fafc' : '#020a07',
                      borderTop: theme === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(56, 248, 137, 0.2)',
                    },
                    footerActionText: {
                      color: theme === 'light' ? '#64748b' : '#94a3b8',
                    },
                    footerActionLink: {
                      color: theme === 'light' ? '#059669' : '#39ff14',
                      "&:hover": {
                        textDecoration: 'underline',
                      },
                    },
                  },
                }}
              />
            </div>
          </SignedIn>
        </div>
      </div>

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <div className="notification-modal-overlay" onClick={handleCloseNotificationModal}>
          <div className="notification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notification-modal__header">
              <div className={`notification-modal__type notification-modal__type--${selectedNotification.type}`}>
                {getNotificationIcon(selectedNotification.type)}
                <span>{NOTIFICATION_TYPE_LABELS[selectedNotification.type] || selectedNotification.type}</span>
              </div>
              <button 
                className="notification-modal__close"
                onClick={handleCloseNotificationModal}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="notification-modal__body">
              <h2 className="notification-modal__title">{selectedNotification.title}</h2>
              
              <div className="notification-modal__meta">
                <div className={`notification-modal__priority notification-modal__priority--${selectedNotification.priority}`}>
                  <Tag size={14} />
                  <span>{PRIORITY_LABELS[selectedNotification.priority] || selectedNotification.priority} Priority</span>
                </div>
                <div className="notification-modal__date">
                  <Calendar size={14} />
                  <span>{formatDate(selectedNotification.createdAt)}</span>
                </div>
              </div>

              <div className="notification-modal__message">
                {selectedNotification.message}
              </div>

              {selectedNotification.docUrl && (
                <a 
                  href={selectedNotification.docUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="notification-modal__doc-link"
                >
                  <ExternalLink size={14} />
                  View Documentation
                </a>
              )}
            </div>

            <div className="notification-modal__footer">
              <button 
                className="notification-modal__btn notification-modal__btn--secondary"
                onClick={handleCloseNotificationModal}
              >
                Close
              </button>
              <button 
                className="notification-modal__btn notification-modal__btn--primary"
                onClick={(e) => handleDocumentationRedirect(selectedNotification, e)}
              >
                <ExternalLink size={14} />
                View Docs
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
